import alertBox from '@/components/generics/alert-box/alert-box.vue';
import codeInput from '@/components/generics/code-input/code-input.vue';
import validators from '@/helpers/validators';
import vuelidateMixin from '@/mixins/vuelidateMixin';

export default {
  name: 'AccountPhoneUpdate',
  components: {
    alertBox,
    codeInput,
  },
  mixins: [vuelidateMixin],
  props: {
    bankLink: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    phoneNumber: validators.phone,
  },
  data() {
    return {
      inputId: `account-phone-update-${Math.random().toString(36).slice(2)}`,
      phoneNumber: '',
      verificationCode: '',
      step: 'input', // 'input', 'verify'
      isSending: false,
      isVerifying: false,
      isDestroyed: false,
      error: null,
      secondsUntilResend: 0,
      resendTimer: null,
    };
  },
  computed: {
    canSendCode() {
      return !this.$v.phoneNumber.$invalid && !this.isSending && this.secondsUntilResend === 0;
    },
    canVerify() {
      return this.verificationCode.length >= 6 && !this.isVerifying;
    },
    phoneError() {
      if (this.$v.phoneNumber.$error) {
        return 'Enter a valid phone number';
      }
      return '';
    },
    inputDescription() {
      return this.bankLink
        ? 'Enter a mobile phone number to receive a verification code.'
        : 'Verify your phone number via SMS code.';
    },
    verificationActionText() {
      return this.bankLink ? 'Verify and Continue' : 'Verify & Update';
    },
    verificationDestination() {
      if (!this.bankLink) return this.phoneNumber;
      const digits = (this.phoneNumber || '').replace(/[^0-9]/g, '');
      if (digits.length < 4) return this.phoneNumber;
      // Non-breaking hyphens keep the masked number on one line on phones.
      return `***‑***‑${digits.slice(-4)}`;
    },
  },
  methods: {
    async sendCode() {
      if (!this.canSendCode) return;

      this.isSending = true;
      this.error = null;

      try {
        const response = await this.$store.dispatch('user/phoneUpdate', {
          phoneNumber: this.phoneNumber,
        });
        if (this.isDestroyed) return;
        this.step = 'verify';
        this.secondsUntilResend = (response && response.seconds_til_resend_available) || 60;
        this.startResendTimer();
        this.$nextTick(() => {
          if (!this.isDestroyed && this.$refs.codeInput) this.$refs.codeInput.focusFirst();
        });
      } catch (err) {
        if (this.isDestroyed) return;
        this.error = this.extractErrorMessage(err, 'Failed to send verification code.');
      } finally {
        if (!this.isDestroyed) {
          this.isSending = false;
        }
      }
    },

    async verifyCode() {
      if (!this.canVerify) return;

      this.isVerifying = true;
      this.error = null;

      try {
        await this.$store.dispatch('user/phoneVerify', {
          phoneNumber: this.phoneNumber,
          code: this.verificationCode,
        });
        if (this.isDestroyed) return;
        // Refresh user data to show updated phone
        await this.$store.dispatch('user/getAccountDetail');
        if (this.isDestroyed) return;
        this.$emit('verified');
      } catch (err) {
        if (this.isDestroyed) return;
        this.error = this.extractErrorMessage(err, 'Incorrect code. Please try again.');
        this.verificationCode = '';
      } finally {
        if (!this.isDestroyed) {
          this.isVerifying = false;
          if (this.error) {
            this.$nextTick(() => {
              if (!this.isDestroyed && this.$refs.codeInput) this.$refs.codeInput.focusFirst();
            });
          }
        }
      }
    },

    startResendTimer() {
      this.clearResendTimer();
      this.resendTimer = setInterval(() => {
        if (this.secondsUntilResend > 0) {
          this.secondsUntilResend -= 1;
        } else {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },

    extractErrorMessage(error, defaultMessage) {
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (Array.isArray(data) && data.length > 0 && data[0].message) {
          return data[0].message;
        }
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0].message;
        }
        if (data.message) {
          return data.message;
        }
      }
      return defaultMessage;
    },

    onCancel() {
      this.$emit('close');
    },
  },
  beforeUnmount() {
    this.isDestroyed = true;
    this.clearResendTimer();
  },
};
